import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Button,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from "reactstrap";
import UpdateDurationModal from "./UpdateDurationModal";

// Lazy load image hook - Optimized to prevent resets
const useLazyImage = (imageSrc) => {
  const [imageSrcState, setImageSrcState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef(null);
  const loadedSrcRef = useRef(null);

  useEffect(() => {
    // Skip if already loaded this exact src
    if (imageSrcState && loadedSrcRef.current === imageSrc) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          imageSrc &&
          imageSrc !== loadedSrcRef.current
        ) {
          const img = new Image();
          img.onload = () => {
            setImageSrcState(imageSrc);
            loadedSrcRef.current = imageSrc;
            setIsLoading(false);
          };
          img.onerror = () => {
            setIsLoading(false);
          };
          img.src = imageSrc;
        }
      },
      { rootMargin: "50px" },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [imageSrc, imageSrcState]);

  return { imageSrcState, isLoading, imgRef };
};
// Lazy Image Component - Memoized to prevent re-renders
const LazyImage = React.memo(({ src, alt, ...props }) => {
  const { imageSrcState, imgRef } = useLazyImage(src);

  return imageSrcState ? (
    <img
      src={imageSrcState}
      alt={alt}
      {...props}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  ) : (
    <div
      ref={imgRef}
      className="placeholder-glow w-100"
      style={{
        height: props?.style?.height || "180px",
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      <div className="placeholder col-12" style={{ height: "100%" }}></div>
    </div>
  );
});

LazyImage.displayName = "LazyImage";

// Memoized Ad Card Component for campaign ads
const AdCard = React.memo(({ ad, isSelected, onToggle, getMediaUrl, onEditDuration }) => {
  const navigate = useNavigate();

  return (
    <Col key={ad.id} xs="12" md="5" lg="2">
      <Card
        className={`media-card border ${
          isSelected ? "border-danger shadow-sm" : ""
        }`}
        style={{ cursor: "pointer" }}
        onClick={() => onToggle(ad)}
      >
        <div className="media-preview position-relative">
          <LazyImage
            src={getMediaUrl(ad)}
            alt={ad.title}
            className="w-100"
            style={{
              height: "180px",
              objectFit: "cover",
            }}
          />
          {isSelected && (
            <Badge
              color="danger"
              pill
              className="position-absolute top-0 end-0 m-2"
            >
              Selected
            </Badge>
          )}
        </div>
        <CardBody>
          {" "}
          <div className="d-flex justify-content-between align-items-center mb-2 align-content-center ">
            <div>
              <h6 className="mb-1 text-truncate">{ad.title} ({ad.duration}s)</h6>
              <small className="text-muted ">{ad.advertiserName}</small>
            </div>

            <div className="d-flex gap-1">
              <Button
                size="sm"
                className="icon-btn"
                title="Update Duration"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditDuration(ad);
                }}
              >
                <i className="ri-time-line"></i>
              </Button>

              <Button
                size="sm"
                className="icon-btn"
                title="Edit Ad"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/ads/${ad.id}`);
                }}
              >
                <i className="ri-edit-line"></i>
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
});

AdCard.displayName = "AdCard";

const MediaTab = ({
  campaign,
  selectedAds,
  setSelectedAds,
  handleUnassign,
  unassigning,
  handleOpenModal,
  modalLoading,
  toggleAdSelection,
  getMediaUrl,
  onDurationUpdate,
  isReorderMode,
  reorderedAds,
  draggedIndex,
  handleReorderDragStart,
  handleReorderDragOver,
  handleReorderDrop,
  handleReorderDragEnd,
}) => {
  const [durationModalOpen, setDurationModalOpen] = useState(false);
  const [selectedAdForDuration, setSelectedAdForDuration] = useState(null);

  const handleEditDuration = (ad) => {
    setSelectedAdForDuration(ad);
    setDurationModalOpen(true);
  };

  const adsToDisplay = isReorderMode ? reorderedAds : campaign.ads;

  return (
    <>
      {adsToDisplay?.length > 0 ? (
        <Row className="g-3">
          {adsToDisplay.map((ad, index) => {
            const isSelected = selectedAds.some((a) => a.id === ad.id);
            
            if (isReorderMode) {
              // Reorder mode with drag functionality
              return (
                <Col key={ad.id} xs="12" md="5" lg="2">
                  <Card
                    className="media-card border position-relative"
                    draggable
                    onDragStart={(e) => handleReorderDragStart(e, index)}
                    onDragOver={handleReorderDragOver}
                    onDrop={(e) => handleReorderDrop(e, index)}
                    onDragEnd={handleReorderDragEnd}
                    style={{
                      cursor: "move",
                      opacity: draggedIndex === index ? 0.5 : 1,
                      transition: "opacity 0.2s",
                    }}
                  >
                    {/* Order Badge */}
                    <Badge
                      color="primary"
                      pill
                      className="position-absolute top-0 start-0 m-2"
                      style={{ zIndex: 10 }}
                    >
                      {index + 1}
                    </Badge>

                    <div className="media-preview position-relative">
                      <LazyImage
                        src={getMediaUrl(ad)}
                        alt={ad.title}
                        className="w-100"
                        style={{
                          height: "180px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 className="mb-1 text-truncate">{ad.title} ({ad.duration}s)</h6>
                          <small className="text-muted">{ad.advertiserName}</small>
                        </div>
                        <i className="ri-draggable text-muted" style={{ fontSize: "1.2rem" }}></i>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            }
            
            // Normal mode
            return (
              <AdCard
                key={ad.id}
                ad={ad}
                isSelected={isSelected}
                onToggle={toggleAdSelection}
                getMediaUrl={getMediaUrl}
                onEditDuration={handleEditDuration}
              />
            );
          })}
        </Row>
      ) : (
        <div className="text-center py-5 text-muted">
          No media associated with this campaign
        </div>
      )}

      <UpdateDurationModal
        isOpen={durationModalOpen}
        toggle={() => setDurationModalOpen(false)}
        ad={selectedAdForDuration}
        campaignId={campaign.id}
        onSuccess={onDurationUpdate}
      />
    </>
  );
};

export default MediaTab;
